import React from 'react';
import { array, arrayOf, bool, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';
import { Field } from 'react-final-form';

import FieldSearchableSelect from '../../../components/FieldSearchableSelect/FieldSearchableSelect';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SearchableSelectFilter.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

const SearchableSelectFilter = props => {
  const {
    rootClassName,
    className,
    showAsPopup,
    options,
    id,
    name,
    label,
    queryParamNames,
    initialValues,
    contentPlacementOffset,
    onSubmit,
    placeholder,
    ...rest
  } = props;

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];

  const classes = classNames(rootClassName || css.root, className);
  const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
  const labelForPlain = <span className={labelClass}>{label}</span>;

  // Pass the initial values with the name key
  const pickedInitialValues = hasInitialValues ? { [name]: initialValues[queryParamName] } : {};

  // For popup mode: receives { [name]: value } from FilterPopup form submit
  const handlePopupSubmit = (values) => {
    const fieldValue = values[name];
    const usedValue = { [queryParamName]: fieldValue || null };
    onSubmit(usedValue);
  };

  // For plain mode: FilterPlain.handleChange calls onSubmit with form values object
  // e.g. { tolLevel2: '25' } — NOT a raw value. Extract the field value correctly.
  const handlePlainSubmit = (values) => {
    const fieldValue = values && typeof values === 'object' ? values[name] : values;
    const usedValue = { [queryParamName]: fieldValue || null };
    onSubmit(usedValue);
  };

  const handleSelectionChange = () => {
    // Submit the form when selection changes in popup mode
    const formElement = document.getElementById(`${id}.popup.form`);
    if (formElement) {
      // Small delay to let field value update
      setTimeout(() => {
        formElement.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }, 10);
    }
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={label}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handlePopupSubmit}
      initialValues={pickedInitialValues}
      keepDirtyOnReinitialize
      showFormButtons={false}
      {...rest}
    >
      <Field
        name={name}
        component={FieldSearchableSelect}
        options={options}
        placeholder={placeholder}
        autoOpen={true}
        onSelectionChange={handleSelectionChange}
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={labelForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handlePlainSubmit}
      initialValues={pickedInitialValues}
      {...rest}
    >
      <Field
        name={name}
        component={FieldSearchableSelect}
        options={options}
        placeholder={placeholder}
      />
    </FilterPlain>
  );
};

SearchableSelectFilter.defaultProps = {
  rootClassName: null,
  className: null,
  showAsPopup: false,
  initialValues: null,
  contentPlacementOffset: 0,
  placeholder: 'Search...',
};

SearchableSelectFilter.propTypes = {
  rootClassName: string,
  className: string,
  showAsPopup: bool,
  id: string.isRequired,
  name: string.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSubmit: func.isRequired,
  options: arrayOf(
    object
  ).isRequired,
  initialValues: object,
  contentPlacementOffset: number,
  placeholder: string,
};

export default SearchableSelectFilter;
